import { motion } from "framer-motion";
import { MapPin, Clock, Phone, MessageCircle, ExternalLink, Instagram } from "lucide-react";
import { PIKKO } from "../../lib/pikko";

const ease = [0.2, 0.8, 0.2, 1];

export default function Location() {
  return (
    <section
      id="lokasi"
      data-testid="location-section"
      className="relative py-24 md:py-32 bg-[var(--pikko-cream)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-12 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, ease }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">— Lokasi</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight text-[var(--pikko-brown-ink)]">
              Visit{" "}
              <span className="italic text-[var(--pikko-orange)]">
                Pikko Pet Care
              </span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, delay: 0.1, ease }}
            className="lg:col-span-5 text-[15px] leading-relaxed text-[var(--pikko-brown-deep)]/85"
          >
            Temukan lokasi Pikko Pet Care di Jatiasih Bekasi untuk layanan
            poliklinik, rawat inap, emergency care, pet hotel, dan grooming
            profesional.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-12 gap-7 lg:gap-9 items-stretch">
          {/* Left: address & CTA */}
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, ease }}
            className="lg:col-span-5 soft-card p-7 lg:p-9 flex flex-col"
          >
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-7 border border-[rgba(138,107,82,0.15)]">
              <img
                src="/images/location.webp"
                alt="Eksterior Pikko Pet Care di Jatimekar Jatiasih Bekasi"
                className="w-full h-full object-cover warm-grade-strong"
                loading="lazy"
              />
            </div>

            <div className="space-y-5">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[var(--pikko-orange)]/12 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4.5 h-4.5 text-[var(--pikko-orange-dark)]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pikko-brown)]/80 font-semibold">
                    Alamat
                  </div>
                  <div className="mt-1 font-serif text-[17px] leading-snug text-[var(--pikko-brown-ink)]">
                    {PIKKO.address}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[var(--pikko-orange)]/12 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4.5 h-4.5 text-[var(--pikko-orange-dark)]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pikko-brown)]/80 font-semibold">
                    Jam Operasional
                  </div>
                  <div className="mt-1 font-serif text-[17px] text-[var(--pikko-brown-ink)]">
                    {PIKKO.hours}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[var(--pikko-orange)]/12 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4.5 h-4.5 text-[var(--pikko-orange-dark)]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pikko-brown)]/80 font-semibold">
                    Kontak
                  </div>
                  <div className="mt-1 font-serif text-[17px] text-[var(--pikko-brown-ink)]">
                    {PIKKO.whatsappNumber}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[var(--pikko-orange)]/12 flex items-center justify-center flex-shrink-0">
                  <Instagram className="w-4.5 h-4.5 text-[var(--pikko-orange-dark)]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pikko-brown)]/80 font-semibold">
                    Instagram
                  </div>
                  <a
                    href={PIKKO.instagramLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 font-serif text-[17px] text-[var(--pikko-brown-ink)] hover:text-[var(--pikko-orange)] transition-colors"
                  >
                    {PIKKO.instagram}
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={PIKKO.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-maps-btn"
                className="btn-primary text-sm"
              >
                <ExternalLink className="w-4 h-4" /> Open in Google Maps
              </a>
              <a
                href={PIKKO.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-whatsapp-btn"
                className="btn-ghost text-sm"
              >
                <MessageCircle className="w-4 h-4" /> Contact via WhatsApp
              </a>
            </div>
          </motion.div>

          {/* Right: Map */}
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, delay: 0.1, ease }}
            className="lg:col-span-7 rounded-[28px] overflow-hidden border border-[rgba(138,107,82,0.15)] shadow-[0_40px_100px_-50px_rgba(46,34,24,0.4)] min-h-[420px] lg:min-h-[100%]"
          >
            <iframe
              title="Pikko Pet Care — Google Maps"
              src={PIKKO.mapsEmbed}
              className="w-full h-full min-h-[460px]"
              style={{ border: 0, filter: "saturate(0.92) contrast(1.02)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="location-map-iframe"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
